import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useBooking, CustomerInfo } from '../../contexts/booking/BookingContext';
import { useAuth } from '../../contexts/auth/AuthContext';
import { supabase } from '../../lib/supabaseClient';
import { isPhoneAuthEmail, normalizePhoneForLookup } from '../../utils/phone';

const CustomerInfoForm: React.FC = () => {
  const navigate = useNavigate();
  const { bookingState, setCustomerInfo, nextStep, prevStep, saveBookingState } = useBooking();
  const { userProfile } = useAuth();
  const { customerInfo } = bookingState;
  const isStaffBooking = userProfile?.role === 'admin' || userProfile?.role === 'crew';

  const [formData, setFormData] = useState<CustomerInfo>({
    firstName: customerInfo?.firstName || '',
    lastName: customerInfo?.lastName || '',
    email: customerInfo?.email || '',
    phone: customerInfo?.phone || '',
    notes: customerInfo?.notes || ''
  });

  const [errors, setErrors] = useState<Partial<Record<keyof CustomerInfo, string>>>({});
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isCheckingEmail, setIsCheckingEmail] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showPhoneLoginModal, setShowPhoneLoginModal] = useState(false);
  const [isPhoneAuthUser, setIsPhoneAuthUser] = useState(false);

  // Check if user is logged in and fetch profile data
  useEffect(() => {
    const fetchUserProfile = async () => {
      try {
        setIsLoading(true);

        // In rebook mode, customerInfo is already set by initEditMode — use it directly
        if (bookingState.editingAppointmentId && customerInfo?.firstName) {
          setFormData({
            firstName: customerInfo.firstName,
            lastName: customerInfo.lastName,
            email: customerInfo.email,
            phone: customerInfo.phone,
            notes: customerInfo.notes || '',
          });
          setIsLoggedIn(true);
          setIsLoading(false);
          return;
        }

        // Admin "book for user" from User Management — pre-fill from target user's profile
        if (bookingState.bookingForClientId) {
          const { data: targetProfile } = await supabase
            .from('user_profiles')
            .select('full_name, preferred_name, email, phone_number')
            .eq('id', bookingState.bookingForClientId)
            .single();

          if (targetProfile) {
            const nameParts = targetProfile.full_name ? targetProfile.full_name.split(' ') : ['', ''];
            const firstName = targetProfile.preferred_name || nameParts[0] || '';
            const lastName = nameParts.slice(1).join(' ') || '';

            setFormData({
              firstName,
              lastName,
              email: targetProfile.email || '',
              phone: targetProfile.phone_number || '',
              notes: '',
            });
            setIsLoggedIn(true);
            setIsLoading(false);
            return;
          }
          // If clientId invalid (no profile), fall through to normal staff booking
        }

        // Check if user is authenticated
        const { data: { user } } = await supabase.auth.getUser();

        if (user) {
          setIsLoggedIn(true);

          // Admin/crew booking on behalf — leave form blank so they type guest info
          if (isStaffBooking) {
            setFormData({
              firstName: '',
              lastName: '',
              email: '',
              phone: '',
              notes: '',
            });
          } else {
            // Fetch user profile for regular users
            const { data: profile, error } = await supabase
              .from('user_profiles')
              .select('*')
              .eq('id', user.id)
              .single();

            if (error) {
              console.error('Error fetching user profile:', error);
              // We don't need to create a profile here - that will happen during booking
            } else if (profile) {
              // Pre-fill form with profile data
              const nameParts = profile.full_name ? profile.full_name.split(' ') : ['', ''];
              const firstName = profile.preferred_name || nameParts[0] || '';
              const lastName = nameParts.slice(1).join(' ') || '';

              // Check if user is a phone auth user
              const isPhoneUser = isPhoneAuthEmail(user.email);
              setIsPhoneAuthUser(isPhoneUser);

              setFormData({
                firstName,
                lastName,
                // Phone auth users see blank email field (pseudo-email hidden)
                email: isPhoneUser ? '' : (profile.email || user.email || ''),
                phone: profile.phone_number || '',
                notes: ''
              });
            }
          }
        } else {
          // User is not logged in - leave form empty for manual entry
          setIsLoggedIn(false);
        }
      } catch (err) {
        console.error('Error checking authentication:', err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchUserProfile();
  }, []);

  // Handle form input changes
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when field is edited
    if (errors[name as keyof CustomerInfo]) {
      setErrors(prev => ({
        ...prev,
        [name]: undefined
      }));
    }
  };

  // Validate form
  const validateForm = (): boolean => {
    const newErrors: Partial<Record<keyof CustomerInfo, string>> = {};
    
    if (!formData.firstName.trim()) {
      newErrors.firstName = 'First name is required';
    }
    
    if (!formData.lastName.trim()) {
      newErrors.lastName = 'Last name is required';
    }
    
    // Email is optional - only validate format if provided
    if (formData.email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }
    
    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone number is required';
    } else if (!/^\d{10,15}$/.test(formData.phone.replace(/\D/g, ''))) {
      newErrors.phone = 'Please enter a valid phone number';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Check if email belongs to a registered user
  const checkRegisteredEmail = async (email: string): Promise<boolean> => {
    try {
      const { data, error } = await supabase.rpc('is_registered_email', {
        p_email: email
      });

      if (error) {
        console.error('Error checking email:', error);
        return false;
      }

      return data === true;
    } catch (err) {
      console.error('Error checking email:', err);
      return false;
    }
  };

  // Check if phone belongs to a registered user
  const checkRegisteredPhone = async (phone: string): Promise<boolean> => {
    try {
      // Normalize phone to E.164 format
      const normalized = normalizePhoneForLookup(phone);
      if (!normalized) return false;

      const { data, error } = await supabase.rpc('is_registered_phone', {
        p_phone: normalized
      });

      if (error) {
        console.error('Error checking phone:', error);
        return false;
      }

      return data === true;
    } catch (err) {
      console.error('Error checking phone:', err);
      return false;
    }
  };

  // Handle login redirect
  const handleLoginRedirect = () => {
    // Save current booking state before redirecting
    setCustomerInfo(formData);
    saveBookingState();

    // Store a flag to indicate we need to restore after login
    localStorage.setItem('lotus_booking_restore', 'true');

    // Redirect to login with return URL
    navigate('/login?redirect=/booking');
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    // If user is logged in, proceed directly
    // Staff booking on behalf — skip login-prompt checks, just proceed
    if (isLoggedIn) {
      setCustomerInfo(formData);
      nextStep();
      return;
    }

    // Check if email is registered (only if email provided and not a phone auth user)
    // Skip for staff — they're already logged in and booking on behalf
    if (!isStaffBooking && !isPhoneAuthUser && formData.email.trim()) {
      setIsCheckingEmail(true);
      const isEmailRegistered = await checkRegisteredEmail(formData.email);
      setIsCheckingEmail(false);

      if (isEmailRegistered) {
        setShowLoginModal(true);
        return;
      }
    }

    // Check if phone is registered (for guest users)
    // Skip for staff — they're already logged in and booking on behalf
    if (!isStaffBooking && formData.phone.trim()) {
      setIsCheckingEmail(true); // Reuse loading state
      const isPhoneRegistered = await checkRegisteredPhone(formData.phone);
      setIsCheckingEmail(false);

      if (isPhoneRegistered) {
        setShowPhoneLoginModal(true);
        return;
      }
    }

    // Proceed with guest booking
    setCustomerInfo(formData);
    nextStep();
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center p-8">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#B2D8C5]"></div>
      </div>
    );
  }

  return (
    <div>
      {bookingState.bookingForClientId && formData.firstName && (
        <div className="mb-4 bg-blue-50 border border-blue-200 rounded-md p-3 flex items-center gap-2">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-blue-600 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
          </svg>
          <p className="text-sm text-blue-800">
            Booking for: <strong>{formData.firstName} {formData.lastName}</strong>
          </p>
        </div>
      )}
      <h2 className="text-2xl font-serif text-[#232347] mb-2">Your Information</h2>
      <p className="text-gray-600 mb-6">Please provide your contact details for your appointment</p>
      
      <form onSubmit={handleSubmit}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          {/* First Name */}
          <div>
            <label htmlFor="firstName" className="block text-[#232347] font-medium mb-1">
              First Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              id="firstName"
              name="firstName"
              value={formData.firstName}
              onChange={handleChange}
              className={`w-full p-2 border rounded-md focus:outline-none focus:ring-2 ${
                errors.firstName 
                  ? 'border-red-500 focus:ring-red-200' 
                  : 'border-gray-300 focus:ring-[#B2D8C5]/50'
              }`}
            />
            {errors.firstName && (
              <p className="text-red-500 text-sm mt-1">{errors.firstName}</p>
            )}
          </div>
          
          {/* Last Name */}
          <div>
            <label htmlFor="lastName" className="block text-[#232347] font-medium mb-1">
              Last Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              id="lastName"
              name="lastName"
              value={formData.lastName}
              onChange={handleChange}
              className={`w-full p-2 border rounded-md focus:outline-none focus:ring-2 ${
                errors.lastName 
                  ? 'border-red-500 focus:ring-red-200' 
                  : 'border-gray-300 focus:ring-[#B2D8C5]/50'
              }`}
            />
            {errors.lastName && (
              <p className="text-red-500 text-sm mt-1">{errors.lastName}</p>
            )}
          </div>
          
          {/* Email */}
          <div>
            <label htmlFor="email" className="block text-[#232347] font-medium mb-1">
              Email <span className="text-gray-500 font-normal">(optional)</span>
            </label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              disabled={isPhoneAuthUser}
              placeholder={isPhoneAuthUser ? '' : 'your@email.com'}
              className={`w-full p-2 border rounded-md focus:outline-none focus:ring-2 ${
                isPhoneAuthUser
                  ? 'bg-gray-100 cursor-not-allowed border-gray-200'
                  : errors.email
                    ? 'border-red-500 focus:ring-red-200'
                    : 'border-gray-300 focus:ring-[#B2D8C5]/50'
              }`}
            />
            {isPhoneAuthUser && (
              <p className="text-gray-500 text-xs mt-1">
                Phone accounts cannot add email during booking
              </p>
            )}
            {errors.email && (
              <p className="text-red-500 text-sm mt-1">{errors.email}</p>
            )}
          </div>
          
          {/* Phone */}
          <div>
            <label htmlFor="phone" className="block text-[#232347] font-medium mb-1">
              Phone <span className="text-red-500">*</span>
            </label>
            <input
              type="tel"
              id="phone"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              className={`w-full p-2 border rounded-md focus:outline-none focus:ring-2 ${
                errors.phone 
                  ? 'border-red-500 focus:ring-red-200' 
                  : 'border-gray-300 focus:ring-[#B2D8C5]/50'
              }`}
            />
            {errors.phone && (
              <p className="text-red-500 text-sm mt-1">{errors.phone}</p>
            )}
            <p className="text-gray-500 text-xs mt-1 leading-relaxed">
              By providing your phone number, you consent to receive SMS booking
              confirmations and reminders from Lotus Healing Spa. Reply STOP to
              opt out. Msg & data rates may apply.
            </p>
          </div>
        </div>
        
        {/* Notes/Special Requests */}
        <div className="mb-6">
          <label htmlFor="notes" className="block text-[#232347] font-medium mb-1">
            Special Requests or Notes (optional)
          </label>
          <textarea
            id="notes"
            name="notes"
            value={formData.notes}
            onChange={handleChange}
            rows={4}
            className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#B2D8C5]/50"
            placeholder="Any special requests, health concerns, or preferences we should know about..."
          />
        </div>
        
        {/* Account creation prompt for non-logged in users */}
        {!isLoggedIn && (
          <div className="mb-6 bg-[#F1F7F4] p-4 rounded-md">
            <h3 className="text-lg font-medium text-[#232347] mb-2">Create an Account?</h3>
            <p className="text-gray-600 mb-3">
              Create an account to easily manage your appointments and get special offers!
            </p>
            <a 
              href="/signup" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-[#B2D8C5] font-medium hover:underline"
            >
              Sign up now
            </a>
          </div>
        )}
        
        {/* Navigation buttons */}
        <div className="mt-8 flex justify-between">
          <button
            type="button"
            onClick={prevStep}
            className="px-6 py-2 rounded-md border border-[#B2D8C5] text-[#232347] hover:bg-[#F1F7F4] transition-colors"
          >
            Back
          </button>
          <button
            type="submit"
            disabled={isCheckingEmail}
            className="px-6 py-2 rounded-md text-white bg-[#B2D8C5] hover:bg-[#88bfa3] transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {isCheckingEmail ? (
              <>
                <span className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full"></span>
                Checking...
              </>
            ) : (
              'Continue'
            )}
          </button>
        </div>
      </form>

      {/* Login Modal */}
      {showLoginModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-[#F1F7F4] rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-[#B2D8C5]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-[#232347] mb-2">Account Found</h3>
              <p className="text-gray-600">
                This email is already registered. Please log in to complete your booking.
              </p>
              <p className="text-sm text-gray-500 mt-2">
                Your selections have been saved and will be restored after login.
              </p>
            </div>

            <div className="space-y-3">
              <button
                onClick={handleLoginRedirect}
                className="w-full px-6 py-3 rounded-md text-white bg-[#B2D8C5] hover:bg-[#88bfa3] transition-colors font-medium"
              >
                Log In to Continue
              </button>
              <button
                onClick={() => setShowLoginModal(false)}
                className="w-full px-6 py-3 rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Use a Different Email
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Phone Login Modal */}
      {showPhoneLoginModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-[#F1F7F4] rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-[#B2D8C5]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-[#232347] mb-2">Account Found</h3>
              <p className="text-gray-600">
                This phone number is already registered. Please log in to complete your booking.
              </p>
              <p className="text-sm text-gray-500 mt-2">
                Your selections have been saved and will be restored after login.
              </p>
            </div>

            <div className="space-y-3">
              <button
                onClick={handleLoginRedirect}
                className="w-full px-6 py-3 rounded-md text-white bg-[#B2D8C5] hover:bg-[#88bfa3] transition-colors font-medium"
              >
                Log In to Continue
              </button>
              <button
                onClick={() => setShowPhoneLoginModal(false)}
                className="w-full px-6 py-3 rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Use a Different Phone Number
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomerInfoForm;
